import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './Contact.css';

function Contact() {
  useEffect(() => {
    document.title = 'Contactanos - ProCam360';
    return () => {
      document.title = 'ProCam360 - Renta de Photobooths 360°'; // Título por defecto
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [captcha, setCaptcha] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCaptcha = (value) => {
    setCaptcha(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar captcha
    if (!captcha) {
      alert(
        'Por favor, completa el captcha para verificar que no eres un robot.'
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Crear FormData para enviar a Formspree
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('g-recaptcha-response', captcha);

      // Enviar a Formspree (CAMBIAR POR TU URL DE FORMSPREE)
      const response = await fetch('https://formspree.io/f/xdkzwwgv', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Limpiar formulario
        setFormData({ name: '', email: '', phone: '', message: '' });
        setCaptcha(null);
        captchaRef.current.reset();

        // También enviar por WhatsApp como backup
        const phone = '525522632166';
        const whatsappMessage = `¡Nuevo contacto desde la web!

📧 Nombre: ${formData.name}
📧 Email: ${formData.email}
📱 Teléfono: ${formData.phone}
💬 Mensaje: ${formData.message}`;

        setTimeout(() => {
          window.open(
            `https://wa.me/${phone}?text=${encodeURIComponent(
              whatsappMessage
            )}`,
            '_blank'
          );
        }, 2000);
      } else {
        throw new Error('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1>¡Contáctanos!</h1>
          <p>
            ¿Listo para hacer de tu evento algo inolvidable? Escríbenos y te
            responderemos súper rápido.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="contact-content">
          <div className="contact-form-section">
            <h2>Cuéntanos sobre tu evento</h2>

            {submitStatus === 'success' && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                <h3>¡Mensaje enviado correctamente!</h3>
                <p>
                  Te responderemos por correo en menos de 24 horas. También te
                  enviamos los datos por WhatsApp como respaldo.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                <h3>Oops, algo salió mal</h3>
                <p>
                  No pudimos enviar tu mensaje. Por favor, inténtalo de nuevo o
                  contáctanos directamente por WhatsApp.
                </p>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Tu correo electrónico"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Tu teléfono"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Cuéntanos sobre tu evento: fecha, tipo de celebración, número de invitados..."
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="form-group captcha-group">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey="6LeeIGsrAAAAAPMs73v10DHBZTkIR24_zkWGW77B"
                  onChange={handleCaptcha}
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary btn-glow ${
                  isSubmitting ? 'loading' : ''
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Enviando...
                  </>
                ) : (
                  '¡Enviar mensaje!'
                )}
              </button>
            </form>
          </div>

          <div className="contact-info-section">
            <h2>Otras formas de contactarnos</h2>
            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-icon">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div className="method-info">
                  <h3>WhatsApp</h3>
                  <p>¡La forma más rápida!</p>
                  <a
                    href="https://wa.me/525522632166"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +52 55 2263 2166
                  </a>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="method-info">
                  <h3>Correo</h3>
                  <p>Te respondemos en menos de 24 horas</p>
                  <a href="mailto:ventas@procam360.mx">ventas@procam360.mx</a>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <i className="fab fa-instagram"></i>
                </div>
                <div className="method-info">
                  <h3>Redes Sociales</h3>
                  <p>Síguenos para ver más contenido</p>
                  <div className="social-links">
                    <a
                      href="https://www.facebook.com/procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Facebook
                    </a>
                    <a
                      href="https://instagram.com/procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Instagram
                    </a>
                    <a
                      href="https://tiktok.com/@procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      TikTok
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
